import React, { useState, useRef, createContext, useContext } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Login } from './Login';
interface AuthContextValue {
  isAuthenticated: boolean;
  // Whether the user has passed the first-launch welcome screen (either by
  // signing in or choosing to continue as a guest).
  hasEntered: boolean;
  enterAsGuest: () => void;
  // Whether the user has verified their identity (scanned/uploaded ID).
  idVerified: boolean;
  markIdVerified: () => void;
  // Run `action` if signed in; otherwise prompt registration and run it after.
  requireAuth: (action?: () => void) => void;
  openLogin: () => void;
  logout: () => void;
}
const AuthContext = createContext<AuthContextValue | null>(null);
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // Logged out by default. Browsing Discover + search is open, but viewing
  // product details and using the menu require signing in + phone verification.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const [idVerified, setIdVerified] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);
  const markIdVerified = () => setIdVerified(true);
  const enterAsGuest = () => setHasEntered(true);
  const requireAuth = (action?: () => void) => {
    if (isAuthenticated) {
      action?.();
      return;
    }
    pendingAction.current = action ?? null;
    setIsLoginOpen(true);
  };
  const openLogin = () => {
    pendingAction.current = null;
    setIsLoginOpen(true);
  };
  const completeLogin = (verified?: boolean) => {
    setIsAuthenticated(true);
    setHasEntered(true);
    if (verified) setIdVerified(true);
    setIsLoginOpen(false);
    const action = pendingAction.current;
    pendingAction.current = null;
    // Run the gated action shortly after the modal closes
    if (action) setTimeout(action, 150);
  };
  const logout = () => setIsAuthenticated(false);
  return <AuthContext.Provider value={{
    isAuthenticated,
    hasEntered,
    enterAsGuest,
    idVerified,
    markIdVerified,
    requireAuth,
    openLogin,
    logout
  }} data-id="element-50">
      {children}
      <AnimatePresence data-id="element-51">
        {isLoginOpen && <Login onLogin={completeLogin} onClose={() => {
        pendingAction.current = null;
        setIsLoginOpen(false);
      }} data-id="element-52" />}
      </AnimatePresence>
    </AuthContext.Provider>;
}